import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import * as moment from 'moment';
import * as _ from 'lodash';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import {FarmSiteService} from '../../../../../services/farmSite.service';
import BusinessEntity from '../../../../../services/businessEntity.model';
import FarmSiteModel from '../../../../../services/farmSite.model';
import {OnHandQuantityService} from '../../../../../services/onHandQuantity.service';
import OnHandQuantitiesModel from '../../../../../services/onHandQuantity.model';
import {ActivatedRoute} from '@angular/router';
import {ExcelService} from 'src/app/services/excelService';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from 'src/app/Utils/batch-loader.util';

declare var $: any;

/**
 * MaterialMovementRegisterComponent
 *
 * This component is responsible for generating and displaying the Material Movement Register report.
 * It provides a user interface for selecting business, farm site, and date range to view detailed
 * material movement logs, including on-hand quantities and transaction details.
 *
 * Key Responsibilities:
 * - Fetch and display material movement data based on selected criteria.
 * - Provide hierarchical selection (business → farm site).
 * - Filter and search report data by item name or date range.
 * - Export report data to Excel.
 * - Implement infinite scrolling for efficient data display.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * User Roles:
 * - Farmer: Can view reports for their own farm business.
 * - Farm Manager: Can view reports for assigned farm business.
 * - Business Admin (Farm): Can view reports for all farms in their business.
 * - Business Admin (Provider): Can view reports for all farmer businesses.
 * - Employee: Can view reports for all businesses (with search).
 *
 * Features:
 * - **Report Generation**: View material movement logs for specific sites and dates.
 * - **Dynamic Filtering**: Filter by item name or date range.
 * - **Excel Export**: Download the report data in Excel format.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Role-based Access**: Restricts data access based on user role and business category.
 * - **Fullscreen View**: View report data in fullscreen mode.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Loads business and farm site data based on user role.
 * 3. User selects business and farm site.
 * 4. `getAllMMRegisterData()` fetches report data from `OnHandQuantityService`.
 * 5. Data is displayed in a table with infinite scroll support.
 * 6. User can filter, search, or export the data.
 *
 * @component
 * @selector app-material-movement-register
 * @example
 * <app-material-movement-register></app-material-movement-register>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-material-movement-register',
  templateUrl: './material-movement-register.component.html',
  styleUrls: ['./material-movement-register.component.scss']
})
export class MaterialMovementRegisterComponent implements OnInit, OnDestroy {
  /**
   * Array of business entities available for selection.
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * Array of farm sites associated with the selected business.
   * @type {Array<FarmSiteModel>}
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * Array of on-hand quantity records (report data).
   * @type {Array<OnHandQuantitiesModel>}
   */
  public onHandQuantities: Array<OnHandQuantitiesModel>;

  /**
   * Model for the current on-hand quantity report request.
   * @type {OnHandQuantitiesModel}
   */
  public onHandQuantity: OnHandQuantitiesModel;

  /**
   * Duplicate array of on-hand quantity records (for filtering reference).
   * @type {Array<OnHandQuantitiesModel>}
   */
  public dupOnHandQuantities: Array<OnHandQuantitiesModel>;

  /**
   * Flag indicating if the page data has finished loading.
   * @type {boolean}
   * @default false
   */
  public pgLoad = false;

  /**
   * Role of the logged-in user.
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Name of the logged-in user.
   * @type {string}
   */
  public LoginUserName: string;

  /**
   * Business name of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessName: string;

  /**
   * Business category of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessCategory: string;

  /**
   * Flag indicating if a search has been executed.
   * @type {boolean}
   */
  public isSearchClicked: boolean;

  /**
   * Flag for sorting order (ascending/descending).
   * @type {boolean}
   * @default false
   */
  public mmregisterAsc: boolean = false;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  public responseLoader: boolean;

  /**
   * Active page number.
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Application permissions object.
   * @type {any}
   */
  public appPermission: any;

  /**
   * Current search query string.
   * @type {string}
   */
  public search: string;

  /**
   * Keyword used for filtering/searching.
   * @type {string}
   */
  public keyword: string;

  /**
   * Generic item property.
   * @type {any}
   */
  public item: any;

  /**
   * Flag to trigger clearing of search input.
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /**
   * Reactive form group for report criteria selection.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag for business dropdown open/close state.
   * @type {boolean}
   * @default false
   */
  public isDropdownOpen = false;

  /**
   * Filtered options for business dropdown.
   * @type {any[]}
   */
  public filteredOptions: any[] = [];

  /**
   * Placeholder text for business search input.
   * @type {string}
   * @default 'Enter the business name'
   */
  public businessSearch: string = 'Enter the business name';

  /**
   * Flag indicating if the form has been submitted.
   * @type {boolean}
   */
  public submitted: boolean;

  /*date range*/
  /**
   * Maximum selectable date (today).
   * @type {moment.Moment}
   */
  maxDate = moment();

  /**
   * Selected date range object.
   * @type {{ startDate: moment.Moment, endDate: moment.Moment }}
   */
  selected: { startDate: moment.Moment, endDate: moment.Moment };

  /**
   * Flag to disable the submit button during async operations.
   * @type {boolean}
   */
  public submitButtonDisabled: boolean;

  /**
   * Flag indicating if filters have been applied.
   * @type {boolean}
   */
  public filtersApplied: boolean;

  /**
   * Flag indicating if the submit button was clicked.
   * @type {boolean}
   */
  public isSubmitClicked: boolean;

  /**
   * Flag indicating if a date filter is applied.
   * @type {boolean}
   */
  public dateFilterApplied: boolean;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Reactive form group for date range selection.
   * @type {FormGroup}
   */
  dateRangeForm!: FormGroup;

  /**
   * Constructor for MaterialMovementRegisterComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - businessfield: Required.
   * - locationField: Required.
   *
   * @constructor
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {ExcelService} excelService - Service for Excel export operations.
   * @service ExcelService
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {FarmSiteService} farmSitesService - Service for Farm Site API operations.
   * @service FarmSiteService
   * @param {OnHandQuantityService} onHandQuantityService - Service for On-Hand Quantity API operations.
   * @service OnHandQuantityService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public excelService: ExcelService, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public onHandQuantityService: OnHandQuantityService,
              public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.onHandQuantity = new OnHandQuantitiesModel();
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required]
    });
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Retrieves user details from local storage.
   * 3. Resets component state flags.
   * 4. Loads business and farm site data based on user role (Farmer vs Admin vs Provider).
   * 5. Auto-selects farm site if only one is available.
   * 6. Loads business entities for selection if user is an employee/admin.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.search = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      this.dateFilterApplied = false;
      this.initializedatePicker();
      window.scrollTo(0, 0);
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      switch (localStorage.getItem('loginBusinessCategory')) {
        case 'Farm':
          this.onHandQuantity.businessId = localStorage.businessID;
          this.registerForm.get('businessfield').setValidators(null);
          this.registerForm.get('businessfield').updateValueAndValidity();
          switch (this.loginPersonType) {
            case 'Business Admin':
            case 'Farmer':
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(localStorage.businessID);
              break;
            default:
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
              break;
          }
          if (this.farmSites.length === 1) {
            this.onHandQuantity.farmSiteId = this.farmSites[0].id; // model-binding key
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.businessEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Getter for easy access to registerForm controls.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * Filters business options based on search text.
   *
   * @method filterBusinesses
   * @returns {void}
   * @side-effect Updates filteredOptions array.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles business selection from dropdown.
   * Sets the selected business ID and fetches associated farm sites.
   *
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity.
   * @returns {Promise<void>}
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.onHandQuantity.businessId = business.id;
    if (this.onHandQuantity.businessId) {
      this.onHandQuantity.farmSiteId = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.onHandQuantity.businessId);
      if (this.farmSites.length === 1) {
        this.onHandQuantity.farmSiteId = this.farmSites[0].id; // model-binding key
      }
    }
  }

  /* Common filter method for search type, button type and combination of both to filter Sales Order Records */
  /**
   * Applies filters and search to the report data.
   *
   * - Handles search text filtering.
   * - Handles date range filtering.
   * - Updates `onHandQuantities` or resets batch loader.
   *
   * @method onFiltersApplied
   * @returns {void}
   */
  public onFiltersApplied() {
    this.onHandQuantities = [];
    this.isFiltering = true;
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.dateFilterApplied) {
        this.applySearchWithFilterType(value);
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.dateFilterApplied) {
        this.applyFilterWithFilterType();
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * Filters report data based on search text.
   *
   * @private
   * @method applySearchFilter
   * @param {string} searchValue - The search text.
   */
  private applySearchFilter(searchValue: string) {
    this.onHandQuantities = this.dupOnHandQuantities.filter((onHandDetailsObj: any) =>
      ['item_name'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], onHandDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * Filters report data based on selected date range.
   *
   * @private
   * @method applyFilterWithFilterType
   */
  private applyFilterWithFilterType() {
    if (this.selected && this.selected.startDate && this.selected.endDate) {
      const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
      const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
      this.onHandQuantities = this.dupOnHandQuantities.filter((onHandDetailsObj: any) =>
        new Date(onHandDetailsObj.transaction_date.split('T')[0]) >= startDate && new Date(onHandDetailsObj.transaction_date.split('T')[0]) <= endDate
      );
    } else {
      this.dateFilterApplied = false;
      this.onFiltersApplied();
    }
  }

  /**
   * Filters report data based on search text and selected date range.
   *
   * @private
   * @method applySearchWithFilterType
   * @param {string} searchValue - The search text.
   */
  private applySearchWithFilterType(searchValue: string) {
    if (this.selected && this.selected.startDate && this.selected.endDate) {
      const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
      const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
      this.onHandQuantities = this.dupOnHandQuantities.filter((onHandDetailsObj: any) => (new Date(onHandDetailsObj.transaction_date.split('T')[0]) >= startDate
          && new Date(onHandDetailsObj.transaction_date.split('T')[0]) <= endDate) && ['item_name'].some(property => {
          const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], onHandDetailsObj);
          return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
        })
      );
    } else {
      this.dateFilterApplied = false;
      const searchValue = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
      this.applySearchFilter(searchValue);
    }
  }

  /**
   * Clears applied filters and resets the view.
   *
   * @method clearFilters
   * @param {string} filterFrom - The source of the clear action ('search' or 'default').
   * @returns {void}
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.dateFilterApplied = false;
        this.selected = undefined;
        this.onFiltersApplied();
        break;
    }
  }

  /*get movement register data by farmer or provider*/
  /**
   * Fetches the material movement register data.
   *
   * - Validates the form.
   * - Prepares the request body with business, farm site, and date.
   * - Calls `onHandQuantityService.getMmRegister`.
   * - Initializes the batch loader with the fetched data.
   *
   * @async
   * @method getAllMMRegisterData
   * @returns {Promise<void>}
   */
  public async getAllMMRegisterData() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.isSubmitClicked = false;
    this.dateFilterApplied = false;
    this.selected = undefined;
    this.search = '';
    this.onHandQuantities = [];
    this.dupOnHandQuantities = [];
    this.isFiltering = false;
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      const reqBody = {
        "businessId": this.onHandQuantity.businessId,
        "farmSiteId": this.onHandQuantity.farmSiteId,
        "businessName": this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
        "production_unit_id": "null",
        "as_of_date": (moment(new Date()).format('YYYY-MM-DD')).toString(),
        "isBH": 0
      }
      this.dupOnHandQuantities = _.orderBy(await this.onHandQuantityService.getMmRegister(reqBody), ['transaction_date'], ['desc']);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.submitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
      this.clearDateRange();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.responseLoader = false;
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.dupOnHandQuantities,
      (updatedList) => {
        this.onHandQuantities = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Exports the material movement register report to Excel.
   *
   * @async
   * @method exportExcel
   * @returns {Promise<void>}
   */
  public async exportExcel() {
    try {
      const reqBody = {
        "businessId": this.onHandQuantity.businessId,
        "farmSiteId": this.onHandQuantity.farmSiteId,
        "businessName": this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
        "farmNameAtLocation": this.farmSites.find((farmSiteObj: FarmSiteModel) => farmSiteObj.id == this.onHandQuantity.farmSiteId).farmNameAtLocation,
        "as_of_date": (moment(new Date()).format('YYYY-MM-DD')).toString(),
        "isBH": 0,
        "production_unit_id": "null",
        "personName": localStorage.getItem('LoginUserName'),
        "mobileNumber": localStorage.getItem('loginPersonMobileNumber')
      }
      const fileObj = this.onHandQuantityService.excelReportsDownload('materialMovement', reqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Material Movement Register Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Opens the report view in fullscreen mode.
   *
   * @method openFullScreen
   * @returns {void}
   */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  // sorting
  /**
   * Sorts the report data by a specified column.
   *
   * @method sortingTanks
   * @param {string} elementDate - The property to sort by.
   * @param {any} typeBoolean - The current sort direction boolean.
   * @param {string} type - The sort type identifier.
   */
  public sortingTanks(elementDate: string, typeBoolean: any, type: string) {
    if (type == 'mmregisterAsc') {
      this.mmregisterAsc = !typeBoolean;
    }
    let orderbyval = 'desc';
    if (this.mmregisterAsc) {
      orderbyval = 'asc';
    }
    this.onHandQuantities = _.orderBy(this.onHandQuantities, [elementDate], [orderbyval]);
  }

  /**
   * Initializes the date range picker form.
   *
   * @method initializedatePicker
   */
  public initializedatePicker() {
    this.dateRangeForm = this.formBuilder.group({
      start: [null],
      end: [null]
    });
    this.dateRangeForm.valueChanges.subscribe(val => {
      const {start, end} = val;
      if (!start || !end) {
        this.dateFilterApplied = false;
      }
    });
  }

  /**
   * Handles the closing of the date picker.
   *
   * @method onPickerClosed
   */
  public onPickerClosed(): void {
    const {start, end} = this.dateRangeForm.value;
    if (start && !end) {
      // Only start selected → default end = start
      this.dateRangeForm.patchValue({end: start}, {emitEvent: false});
      this.dateFilterApplied = true;
      this.onDateChanged({startDate: start, endDate: start});
    } else if (start && end) {
      this.dateFilterApplied = true;
      this.onDateChanged({startDate: start, endDate: end});
    } else {
      this.dateFilterApplied = false;
    }
  }

  /**
   * Handles date range changes.
   *
   * @method onDateChanged
   * @param {object} range - The selected date range.
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
    this.onFiltersApplied(); // reuse your logic
  }

  /**
   * Clears the selected date range.
   *
   * @method clearDateRange
   */
  public clearDateRange(): void {
    this.dateRangeForm.reset();
    this.dateFilterApplied = false;
    this.selected = undefined;
    this.onFiltersApplied();
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.onHandQuantityService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
